import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';

const VarleInvoiceForm = () => {
  const [formData, setFormData] = useState({
    date_start: '',
    date_end: ''
  });
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const response = await fetch('https://api.prixora.lt/api/VarleInvoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      setResponse(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'AP': return 'success';
      case 'PA': return 'warning';
      case 'PR': return 'info';
      case 'FN': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'AP': return 'Approved';
      case 'PA': return 'Pending';
      case 'PR': return 'Processing';
      case 'FN': return 'Finished';
      default: return status;
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Card elevation={3}>
        <CardHeader
          title="Varle Invoice Generator"
          subheader="Generate invoices for Varle orders within a date range"
          avatar={<ReceiptIcon color="primary" />}
        />
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  name="date_start"
                  type="date"
                  value={formData.date_start}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  name="date_end"
                  type="date"
                  value={formData.date_end}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  fullWidth
                >
                  {loading ? 'Processing...' : 'Generate Invoices'}
                </Button>
              </Grid>
            </Grid>
          </form>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <strong>Error:</strong> {error}
            </Alert>
          )}

          {response && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Processing Results
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {response.totalOrders}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Orders
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {response.processedOrders}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Processed Orders
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      {response.results?.filter(r => r.success).length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Successful Invoices
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {response.results && response.results.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Order Details
                  </Typography>
                  {response.results.map((result, index) => (
                    <Accordion key={index} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            Order #{result.orderId}
                          </Typography>
                          <Chip
                            label={getStatusLabel(result.orderStatus)}
                            color={getStatusColor(result.orderStatus)}
                            size="small"
                          />
                          {result.success ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <ErrorIcon color="error" fontSize="small" />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Order Information
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Order ID:</strong> {result.orderId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Status:</strong> {getStatusLabel(result.orderStatus)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Date:</strong> {result.varleOrderData?.date ? formatDate(result.varleOrderData.date) : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Total:</strong> {result.varleOrderData?.price?.total ? `€${result.varleOrderData.price.total}` : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Invoice Status
                            </Typography>
                            <Typography variant="body2" color={result.success ? 'success.main' : 'error.main'}>
                              <strong>Status:</strong> {result.success ? 'Success' : 'Failed'}
                            </Typography>
                            {result.error && (
                              <Typography variant="body2" color="error.main">
                                <strong>Error:</strong> {result.error}
                              </Typography>
                            )}
                            {result.errorDetails && (
                              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                                <strong>Details:</strong> {result.errorDetails}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>

                        {result.varleOrderData?.client && (
                          <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>
                              Client Information
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Name:</strong> {result.varleOrderData.client.fullName || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Email:</strong> {result.varleOrderData.client.email || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Phone:</strong> {result.varleOrderData.client.phone || 'N/A'}
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VarleInvoiceForm;